import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, UserCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useBrandProfile } from "@/hooks/useBrandProfile";

/**
 * BrandProfileBanner — dashboard / builder banner that nudges the founder to create or complete
 * their AI Brand Profile. Renders nothing while loading.
 */
export default function BrandProfileBanner({ compact = false }) {
  const { profile, hasProfile, completeness, loading } = useBrandProfile();
  if (loading) return null;

  if (!hasProfile) {
    return (
      <div className="flex flex-col gap-3 rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 p-4 dark:border-violet-900 dark:from-violet-950/40 dark:to-fuchsia-950/30 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-600 text-white"><Sparkles className="h-5 w-5" /></span>
          <div>
            <p className="font-semibold">Create your AI Brand Profile once — every prompt fills itself in.</p>
            {!compact && <p className="text-sm text-muted-foreground">Brand name, positioning, consumer, claims, competitors, voice and colors. Takes about 5 minutes.</p>}
          </div>
        </div>
        <Button asChild className="shrink-0 bg-violet-600 hover:bg-violet-700">
          <Link to="/ai-cpg-tools/brand-profile">Set up Brand Profile <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-600 text-white"><UserCircle2 className="h-5 w-5" /></span>
        <div className="min-w-0">
          <p className="truncate font-semibold">{profile.brandName} <span className="font-normal text-muted-foreground">· {profile.category}</span></p>
          <div className="mt-1 flex items-center gap-2">
            <Progress value={completeness} className="h-1.5 w-40" />
            <span className="text-xs text-muted-foreground">{completeness}% complete</span>
          </div>
        </div>
      </div>
      <Button asChild variant="outline" size="sm" className="shrink-0">
        <Link to="/ai-cpg-tools/brand-profile">{completeness < 70 ? "Complete profile" : "Edit profile"}</Link>
      </Button>
    </div>
  );
}
